import type { ChangeEvent } from 'react';
import React, { useCallback, useState, useMemo } from 'react';
import classNames from 'classnames';
import * as Yup from 'yup';
import Cookies from 'js-cookie';
import type { FormikValues as IFormikValues } from 'formik';
import qs from 'qs';
import { has } from 'lodash';
import { Segment, isValidPostcode } from '@fxtr/i18n';
import { CLOUDINARY_URL } from '$constants/cloudinary';
import { storeStartSessionUrl, isBrowser } from '$util/index';
import type { AnyObject, PageMeta } from '$util/types';
import { InjectComponent } from '$util/InjectComponent';
import { getShortTextByKey, getImageByKey } from '$cms/getDataByKey';
import type { CmsContentDictionary } from '$cms/index';
import FormItem from '$components/composite/Form/FormItem';
import PostcodeInput from '$components/composite/Form/DynamicRoutePostcodeInput';
import Input from '$components/composite/Form/InputCmsIntegrated';
import partners from '$data/Partners';
import { useAppContext } from '$util/AppContext';
import { getCampaignId } from '$util/getCampaignId';
import { getSegmentFromLocale } from '$util/getSegment/getSegmentFromLocale';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { vrmUKRegex, vrmFRRegex } from '$constants/regexp';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { Button } from '$src-components/atoms/Button';
import { getActiveExperiments } from '$util/abTest';
import type { IFormProps } from '$components/composite/Form';
import Form from '$components/composite/Form';
import { CmsHeroSection } from '$quantum/organisms/CmsHeroSection';
import styles from './index.module.scss';

const sanitizeString = (str: string) => str.replace(/\s+/g, '').trim();

/**
 * @TODO: move to its own module
 */
const safeCheckPartnerNameFromCookie = () => {
  const partnerCookie = Cookies.getJSON('fixterPartner');
  if (!partnerCookie || !partners) return undefined;
  const isPartner = partners.find(({ slug }) => slug === partnerCookie);
  if (!isPartner) return undefined;
  return partnerCookie;
};

const postcodeRegex =
  // eslint-disable-next-line max-len
  /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i;
/**
 * Only allows numbers, letters and spaces in VRM field between 2-10 characters.
 */

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getImage = getImageByKey.bind(cmsData);
  return {
    textPostcode: getShortText('vrmLookup.text.postcode', 'Your postcode'),
    textPostcodeRequired: getShortText('vrmLookup.text.postcode.required', 'Enter a correct postcode'),
    textPostcodeInvalid: getShortText('vrmLookup.text.postcode.invalid', 'Invalid postcode'),
    textRegistrationNumber: getShortText('vrmLookup.text.registrationNumber', 'Your registration number'),
    textRegistrationNumberRequired: getShortText(
      'vrmLookup.text.registrationNumber.required',
      'Enter a correct VRM'
    ),
    textRegistrationNumberInvalid: getShortText('vrmLookup.text.registrationNumber.invalid', 'Invalid VRM'),
    textCtaGetInstantQuote: getShortText('vrmLookup.cta.getInstantQuote', 'Get an instant quote'),
    textCtaIsReferral: getShortText('vrmLookup.cta.isReferral', 'Redeem now'),
    textCtaIsFree: getShortText('vrmLookup.cta.isFree', 'Book a Free Check'),
    textCtaIsFromErrorPage: getShortText('vrmLookup.cta.isFromErrorPage', 'Try again'),
    imagePostcode: getImage('vrmLookup.image.postcode', {
      img: { original_secure_url: `${CLOUDINARY_URL}/icons/icon_postcode_vhjmk6.svg`, width: 19, height: 21 },
      alt: 'Postcode',
      title: '',
    }).img.original_secure_url,
    imagePostcodeBlue: getImage('vrmLookup.image.postcodeBlue', {
      img: {
        original_secure_url: `${CLOUDINARY_URL}/icons/icon_postcode_blue_xrhv7b.svg`,
        width: 19,
        height: 21,
      },
      alt: 'Postcode',
      title: '',
    }).img.original_secure_url,
    imageRegistrationNumber: getImage('vrmLookup.image.registrationNumber', {
      img: {
        original_secure_url: `${CLOUDINARY_URL}/icons/icon_vrm_epcna1.svg`,
        width: 26,
        height: 20,
      },
      alt: 'VRM',
      title: '',
    }).img.original_secure_url,
    imageRegistrationNumberBlue: getImage('vrmLookup.image.registrationNumberBlue', {
      img: {
        original_secure_url: `${CLOUDINARY_URL}/icons/icon_vrm_blue_ogdzpf.svg`,
        width: 26,
        height: 20,
      },
      alt: 'VRM',
      title: '',
    }).img.original_secure_url,
  };
};

export interface PageTypeProps {
  readonly slug: string;
  readonly sku?: string;
  readonly type: string;
}

export enum VrmLookupInjectName {
  HEAD = 'head',
}

export interface VrmLookupInjectParams {
  [VrmLookupInjectName.HEAD]: {
    readonly imagePostcode: string;
    readonly imageRegistrationNumber: string;
  };
}

export interface VrmLookupProps {
  readonly disabled?: boolean;
  readonly extraParams?: AnyObject;
  readonly targetBlank?: boolean;
  readonly formAction?: string;
  readonly beforeCTA?: React.ReactNode;
  readonly afterCTA?: React.ReactNode;
  readonly buttonText?: React.ReactNode;
  readonly vrm?: string;
  readonly postcode?: string;
  readonly sku?: string;
  readonly isFree?: boolean;
  readonly isFromErrorPage?: boolean;
  readonly icon?: string;
  readonly iconBlue?: string;
  readonly activeTab?: string;
  readonly enableFloatingInput?: boolean;
  readonly className?: string;
}

export interface VrmLookupAppContext extends AppContextGlobal {
  readonly pageType: PageMeta[];
  readonly pathname: string;
  readonly meta: AnyObject;
}

type AdditionalProps = IFormProps & {
  readonly imageRegistrationNumber?: string;
  readonly imageRegistrationNumberBlue?: string;
  readonly imagePostcode?: string;
  readonly imagePostcodeBlue?: string;
};

function FormWithAdditionalProps(props: AdditionalProps) {
  const {
    imageRegistrationNumber,
    imageRegistrationNumberBlue,
    imagePostcode,
    imagePostcodeBlue,
    ...otherProps
  } = props;

  return <Form {...otherProps} />;
}

export default function VrmLookup({
  disabled,
  extraParams,
  formAction = '/vehicles/add',
  targetBlank = false,
  beforeCTA,
  afterCTA,
  buttonText,
  vrm: queryVrm = '',
  postcode: queryPostcode = '',
  sku,
  isFree,
  isFromErrorPage,
  enableFloatingInput = true,
  activeTab,
  className,
  ...props
}: VrmLookupProps): JSX.Element {
  const cmsPageContext = useCmsPageContext();

  const [submitLoading, setSubmitLoading] = useState(false);

  let skuFromDynamicPage = '';

  const cmsContextSku = cmsPageContext?.pageRoutes?.[0]?.collectionItems?.product?.sku;
  if (cmsContextSku) {
    skuFromDynamicPage = cmsContextSku;
  }
  // if collectionItems aren't available then as a last resort
  // we find a HeroSection component and use its configured sku
  else {
    const heroSectionComponent = cmsPageContext?.components?.find(
      (component) => component.uiComponent === CmsHeroSection.displayName
    );
    if (heroSectionComponent?.configuration?.sku)
      skuFromDynamicPage = heroSectionComponent.configuration.sku as string;
  }
  const AppContext = useAppContext<VrmLookupAppContext>();
  const {
    pageType = [],
    location: { pathname },
    meta = {},
    cmsContent: { dictionary },
    locale,
  } = AppContext;

  const {
    textPostcode,
    textPostcodeRequired,
    textPostcodeInvalid,
    textRegistrationNumber,
    textRegistrationNumberRequired,
    textRegistrationNumberInvalid,
    textCtaGetInstantQuote,
    textCtaIsReferral,
    textCtaIsFree,
    textCtaIsFromErrorPage,
    imagePostcode,
    imagePostcodeBlue,
    imageRegistrationNumber,
    imageRegistrationNumberBlue,
  } = getCmsData(dictionary);

  const segment = getSegmentFromLocale(locale);
  const isReferral = pathname && (pathname.includes('/me/') || pathname.includes('referral'));

  const searchParams = useMemo(() => {
    if (isBrowser) {
      const parse = new URLSearchParams(window.location.search);
      const campaignIdFromUrl = parse.get('campaignId');
      const tier = parse.get('tier');
      return { campaignId: campaignIdFromUrl, tierKapten: tier };
    }
    return {};
  }, []);

  const [{ vrm, postcode }, setState] = useState({ vrm: queryVrm, postcode: queryPostcode });

  const onSubmit = useCallback(
    ({ vrm: sVrm, postcode: sPostcode, latitude, longitude }: any) => {
      storeStartSessionUrl();

      const skuFromPage = pageType?.find((item: PageTypeProps) => item.type === 'service')?.sku;
      const hasAutoAdd = pageType?.find((item: { readonly type: string }) => item.type === 'service');
      const { campaignId, tierKapten } = searchParams;
      const defaultParams = {
        vrm: sVrm,
        postcode: sPostcode,
        latitude,
        longitude,
        campaignId: getCampaignId(pathname, campaignId || meta?.campaignIdFromCode, tierKapten),
        partner: safeCheckPartnerNameFromCookie(),
        sku: sku || skuFromPage || skuFromDynamicPage,
        autoAddToBasket: has(hasAutoAdd, 'autoAddToBasket') || undefined,
        activeTab,
        activeExperiments: getActiveExperiments(),
      };
      let params = defaultParams;
      if (extraParams) {
        params = {
          ...defaultParams,
          ...extraParams,
        };
      }
      const query = qs.stringify(params);
      if (targetBlank) {
        window.open(`${formAction}?${query}`, '_blank');
      } else {
        window.location.href = `${formAction}?${query}`;
      }
    },
    [
      extraParams,
      formAction,
      meta?.campaignIdFromCode,
      pageType,
      pathname,
      searchParams,
      sku,
      skuFromDynamicPage,
      targetBlank,
      activeTab,
    ]
  );

  const handleSubmit = useCallback(
    async ({ vrm: sVrm, postcode: sPostcode }: IFormikValues) => {
      setSubmitLoading(true);

      type SubmitData = {
        vrm: string;
        postcode: string;
      };

      const submitData: SubmitData = {
        vrm: sanitizeString(sVrm),
        postcode: sanitizeString(sPostcode),
      };
      onSubmit(submitData);
    },
    [onSubmit]
  );

  const getValidationSchema = () => {
    if (segment === Segment.Fixter_FR) {
      return Yup.object().shape({
        vrm: Yup.string()
          .trim()
          .matches(vrmFRRegex, textRegistrationNumberInvalid)
          .required(textRegistrationNumberRequired),
        postcode: Yup.mixed()
          .test({
            name: 'postcodeValidation',
            test: (value) => !!value && isValidPostcode(sanitizeString(value), segment),
            message: textPostcodeInvalid,
          })
          .required(textPostcodeRequired),
      });
    }

    return Yup.object().shape({
      vrm: Yup.string()
        .trim()
        .matches(vrmUKRegex, textRegistrationNumberInvalid)
        .required(textRegistrationNumberRequired),
      postcode: Yup.string()
        .uppercase()
        .trim()
        // including +1 white space (trim doesn't seem to work as expected here)
        .max(8, textPostcodeInvalid)
        .matches(postcodeRegex, textPostcodeInvalid)
        .required(textPostcodeRequired),
    });
  };

  const getCtaText = () => {
    if (isReferral) return textCtaIsReferral;
    if (isFree) return textCtaIsFree;
    if (isFromErrorPage) return textCtaIsFromErrorPage;
    return textCtaGetInstantQuote;
  };

  const disableAutocorrect = segment === Segment.Fixter_FR;

  return (
    <>
      {/* Used to inject preload for the images */}
      <InjectComponent<VrmLookupInjectParams[VrmLookupInjectName.HEAD]>
        name={VrmLookupInjectName.HEAD}
        namespace={VrmLookup.displayName}
        params={{ imagePostcode, imageRegistrationNumber }}
      />
      <FormWithAdditionalProps
        {...props}
        formik={{
          initialValues: { vrm: queryVrm, postcode: queryPostcode },
          validationSchema: getValidationSchema(),
          onSubmit: handleSubmit,
        }}
        imagePostcode={imagePostcode}
        imagePostcodeBlue={imagePostcodeBlue}
        imageRegistrationNumber={imageRegistrationNumber}
        imageRegistrationNumberBlue={imageRegistrationNumberBlue}
        className={classNames('VrmLookup', styles.vrmLookupForm, className)}
      >
        <div className={styles['input-group']}>
          <FormItem name="postcode" className={classNames(styles.postcode, 'postcode')}>
            <PostcodeInput
              useAutocomplete={segment === Segment.Fixter_UK}
              placeholder={textPostcode}
              floating={enableFloatingInput && (postcode?.length || queryPostcode?.length) > 0}
              onChange={(e) => setState((prev) => ({ ...prev, postcode: e }))}
              inputType={segment === Segment.Fixter_FR ? 'number' : 'text'}
              spellCheck={!disableAutocorrect}
            />
          </FormItem>
          <FormItem name="vrm" className={classNames(styles.vrm, 'vrm')}>
            <Input
              name="vrm"
              value={vrm}
              placeholder={textRegistrationNumber}
              label={textRegistrationNumber}
              iconId="icon_car"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setState((prev) => ({ ...prev, vrm: e?.target?.value }))
              }
              autoComplete={disableAutocorrect ? 'off' : 'on'}
              spellCheck={!disableAutocorrect}
            />
          </FormItem>
        </div>
        <div className={classNames(styles.footerGroup)}>
          {beforeCTA}
          <Button
            className="-submit"
            size="L"
            type="submit"
            fullWidth
            loading={submitLoading}
            disabled={disabled}
          >
            {buttonText || getCtaText()}
          </Button>
        </div>
      </FormWithAdditionalProps>
    </>
  );
}

VrmLookup.displayName = 'VrmLookup';
